import { tooltipFactory } from '@milkdown/plugin-tooltip';
import { usePluginViewContext } from '@prosemirror-adapter/react';
import { useMemo } from 'react';
import { callCommand } from '@milkdown/kit/utils';
import { Button, ButtonGroup } from '@brainfish-ai/components';
import clsx from 'clsx';

import { setNoticeStyleCommand, NOTICE_STYLES, NOTICE_LABELS, type NoticeStyle } from './notice';
import { NOTICE_NODE_NAME, NOTICE_TOOLTIP_NAME } from './constants';
import { useEditorAction } from '../../hooks/useEditorAction';
import { getNodeAtCursor } from '../../utils/getNodeAtCursor';

/**
 * Create the tooltip plugin using Milkdown's tooltipFactory
 */
export const noticeStyleTooltip = tooltipFactory('NOTICE_STYLE');

/**
 * React component for notice style tooltip
 */
export const NoticeTooltipView = () => {
  const { view, prevState } = usePluginViewContext();
  const { action } = useEditorAction();

  // Compute tooltip state directly from view (no useState to avoid loops)
  const tooltipState = useMemo(() => {
    if (!view || !view.editable) {
      return { show: false, position: { top: 0, left: 0 }, activeStyle: 'info' as NoticeStyle };
    }

    const noticeInfo = getNodeAtCursor(view, NOTICE_NODE_NAME);
    const hasTextSelection = !view.state.selection.empty;

    if (!noticeInfo || hasTextSelection) {
      return { show: false, position: { top: 0, left: 0 }, activeStyle: 'info' as NoticeStyle };
    }

    // Get position relative to editor parent
    const domNode = view.nodeDOM(noticeInfo.pos) as HTMLElement | null;
    const editorParent = view.dom.parentElement;

    if (!domNode || !editorParent) {
      return { show: false, position: { top: 0, left: 0 }, activeStyle: 'info' as NoticeStyle };
    }

    const blockRect = domNode.getBoundingClientRect();
    const parentRect = editorParent.getBoundingClientRect();

    // Position at bottom center of notice block
    const blockCenterX = blockRect.left + blockRect.width / 2 - parentRect.left;

    return {
      show: true,
      position: {
        top: blockRect.bottom - parentRect.top + 8, // bottom of the block + 8px gap
        left: blockCenterX, // center of the block
      },
      activeStyle: noticeInfo.node.attrs.style as NoticeStyle,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, view?.state.selection, prevState]);

  const handleStyleClick = (style: NoticeStyle) => (e: React.MouseEvent) => {
    e.preventDefault();
    action(callCommand(setNoticeStyleCommand.key, style));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const focusedButton = document.activeElement;

    switch (e.key) {
      case 'Escape':
      case 'Tab':
        e.preventDefault();
        view.focus();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        (focusedButton?.previousElementSibling as HTMLElement)?.focus();
        break;
      case 'ArrowRight':
        e.preventDefault();
        (focusedButton?.nextElementSibling as HTMLElement)?.focus();
        break;
    }
  };

  if (!tooltipState.show) return null;

  return (
    <ButtonGroup
      orientation="horizontal"
      data-name={NOTICE_TOOLTIP_NAME}
      className="milkdown-toolbar absolute z-50 gap-2 !px-1.5"
      style={{
        top: `${tooltipState.position.top}px`,
        left: `${tooltipState.position.left}px`,
        transform: 'translateX(-50%)',
      }}
      onKeyDown={handleKeyDown}
    >
      {NOTICE_STYLES.map((style) => {
        const isActive = tooltipState.activeStyle === style;

        return (
          <Button
            key={style}
            variant="ghost"
            size="sm"
            onClick={handleStyleClick(style)}
            className={clsx('toolbar-item !w-auto !mx-auto', isActive && 'active !bg-dark-400 !text-dark-900')}
            aria-selected={isActive}
          >
            <span>{NOTICE_LABELS[style]}</span>
          </Button>
        );
      })}
    </ButtonGroup>
  );
};
